// Admin Panel Theme Configuration - Luxury Edition
// Typography: Cormorant Garamond (display) + DM Sans (body) + JetBrains Mono (code)

export const adminTheme = {
  colors: {
    // Primary Colors - Deep Luxury
    primary: '#1a1a1a',
    primaryLight: '#2d2d2d',
    primaryDark: '#0a0a0a',

    // Accent Colors - Refined Slate
    accent: '#334155',
    accentLight: '#64748b',
    accentDark: '#1e293b',
    accentMuted: 'rgba(51, 65, 85, 0.1)',
    accentSubtle: 'rgba(51, 65, 85, 0.05)',

    // Status Colors - Refined
    success: '#0d9668',
    successLight: '#d1fae5',
    warning: '#d97706',
    warningLight: '#fef3c7',
    error: '#dc2626',
    errorLight: '#fee2e2',
    info: '#2563eb',
    infoLight: '#dbeafe',

    // Warm Neutral Palette
    white: '#ffffff',
    cream: '#fefdfb',
    champagne: '#fbf9f7',
    ivory: '#f8f6f3',
    sand: '#f3f0eb',
    taupe: '#e8e4de',
    warmGray100: '#f5f4f2',
    warmGray200: '#e9e7e3',
    warmGray300: '#d6d3cd',
    warmGray400: '#a8a49c',
    warmGray500: '#7a756c',
    warmGray600: '#5c574f',
    warmGray700: '#3d3935',
    warmGray800: '#292622',
    warmGray900: '#1a1816',

    // Legacy Gray (for compatibility)
    gray50: '#fafafa',
    gray100: '#f5f4f2',
    gray200: '#e9e7e3',
    gray300: '#d6d3cd',
    gray400: '#a8a49c',
    gray500: '#7a756c',
    gray600: '#5c574f',
    gray700: '#3d3935',
    gray800: '#292622',
    gray900: '#1a1816',
    black: '#0a0a0a',

    // Background Colors - Warm
    background: '#fefdfb',
    backgroundSecondary: '#fbf9f7',
    backgroundTertiary: '#f5f4f2',
    backgroundDark: '#1a1816',

    // Border Colors - Subtle
    border: '#e9e7e3',
    borderLight: '#f3f0eb',
    borderDark: '#d6d3cd',
    borderAccent: 'rgba(51, 65, 85, 0.2)',

    // Text Colors - Refined
    text: '#1a1816',
    textSecondary: '#5c574f',
    textTertiary: '#7a756c',
    textMuted: '#a8a49c',
    textInverse: '#fefdfb',
    textAccent: '#334155',

    // Chart Colors - Premium Palette
    chart1: '#334155',  // Slate
    chart2: '#1a1816',  // Charcoal
    chart3: '#7a756c',  // Warm Gray
    chart4: '#0d9668',  // Emerald
    chart5: '#2563eb',  // Royal Blue
    chart6: '#7c3aed',  // Purple
    chart7: '#d97706',  // Amber
    chart8: '#be185d',  // Rose

    // Gradients
    gradientAccent: 'linear-gradient(135deg, #334155 0%, #475569 50%, #334155 100%)',
    gradientDark: 'linear-gradient(135deg, #1a1816 0%, #292622 100%)',
    gradientWarm: 'linear-gradient(180deg, #fefdfb 0%, #fbf9f7 100%)',
    gradientSubtle: 'linear-gradient(180deg, rgba(51, 65, 85, 0.02) 0%, rgba(51, 65, 85, 0) 100%)',

    // Overlay Colors
    overlayLight: 'rgba(254, 253, 251, 0.95)',
    overlayDark: 'rgba(26, 24, 22, 0.8)',
    overlayAccent: 'rgba(51, 65, 85, 0.08)'
  },

  typography: {
    fontFamily: {
      display: '"Cormorant Garamond", "Times New Roman", Georgia, serif',
      sans: '"DM Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      mono: '"JetBrains Mono", ui-monospace, SFMono-Regular, "SF Mono", Consolas, monospace'
    },
    fontSize: {
      xs: '0.75rem',     // 12px
      sm: '0.875rem',    // 14px
      base: '1rem',      // 16px
      lg: '1.125rem',    // 18px
      xl: '1.25rem',     // 20px
      '2xl': '1.5rem',   // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
      '6xl': '3.75rem'   // 60px
    },
    fontWeight: {
      thin: 100,
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
      black: 900
    },
    lineHeight: {
      none: 1,
      tight: 1.2,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 1.8
    },
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em'
    }
  },

  spacing: {
    0: '0',
    px: '1px',
    0.5: '0.125rem', // 2px
    1: '0.25rem',    // 4px
    1.5: '0.375rem', // 6px
    2: '0.5rem',     // 8px
    2.5: '0.625rem', // 10px
    3: '0.75rem',    // 12px
    3.5: '0.875rem', // 14px
    4: '1rem',       // 16px
    5: '1.25rem',    // 20px
    6: '1.5rem',     // 24px
    7: '1.75rem',    // 28px
    8: '2rem',       // 32px
    9: '2.25rem',    // 36px
    10: '2.5rem',    // 40px
    11: '2.75rem',   // 44px
    12: '3rem',      // 48px
    14: '3.5rem',    // 56px
    16: '4rem',      // 64px
    20: '5rem',      // 80px
    24: '6rem',      // 96px
    28: '7rem',      // 112px
    32: '8rem',      // 128px
    36: '9rem',      // 144px
    40: '10rem',     // 160px
    44: '11rem',     // 176px
    48: '12rem',     // 192px
    52: '13rem',     // 208px
    56: '14rem',     // 224px
    60: '15rem',     // 240px
    64: '16rem',     // 256px
    72: '18rem',     // 288px
    80: '20rem',     // 320px
    96: '24rem'      // 384px
  },

  borderRadius: {
    none: '0',
    sm: '0.125rem',  // 2px
    base: '0.25rem', // 4px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    '3xl': '1.5rem', // 24px
    full: '9999px'
  },

  shadows: {
    none: 'none',
    xs: '0 1px 2px 0 rgba(26, 24, 22, 0.03)',
    sm: '0 1px 3px 0 rgba(26, 24, 22, 0.04), 0 1px 2px -1px rgba(26, 24, 22, 0.02)',
    base: '0 2px 4px -1px rgba(26, 24, 22, 0.04), 0 1px 2px -1px rgba(26, 24, 22, 0.02)',
    md: '0 4px 6px -1px rgba(26, 24, 22, 0.05), 0 2px 4px -2px rgba(26, 24, 22, 0.03)',
    lg: '0 10px 15px -3px rgba(26, 24, 22, 0.06), 0 4px 6px -4px rgba(26, 24, 22, 0.04)',
    xl: '0 20px 25px -5px rgba(26, 24, 22, 0.08), 0 8px 10px -6px rgba(26, 24, 22, 0.04)',
    '2xl': '0 25px 50px -12px rgba(26, 24, 22, 0.15)',
    inner: 'inset 0 2px 4px 0 rgba(26, 24, 22, 0.04)',
    // Luxury shadows - refined
    luxurySm: '0 2px 8px -2px rgba(26, 24, 22, 0.06), 0 0 0 1px rgba(51, 65, 85, 0.03)',
    luxuryMd: '0 4px 12px -4px rgba(26, 24, 22, 0.08), 0 0 0 1px rgba(51, 65, 85, 0.05)',
    luxuryLg: '0 8px 24px -8px rgba(26, 24, 22, 0.10), 0 0 0 1px rgba(51, 65, 85, 0.06)',
    // Glow effects
    glowAccent: '0 0 20px rgba(51, 65, 85, 0.15)',
    glowSoft: '0 0 40px rgba(26, 24, 22, 0.05)'
  },

  animation: {
    duration: {
      instant: '0ms',
      fast: '150ms',
      base: '200ms',
      moderate: '300ms',
      slow: '400ms',
      slower: '500ms',
      slowest: '700ms'
    },
    easing: {
      linear: 'linear',
      in: 'cubic-bezier(0.4, 0, 1, 1)',
      out: 'cubic-bezier(0, 0, 0.2, 1)',
      inOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      // Premium easings
      spring: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      smooth: 'cubic-bezier(0.25, 0.1, 0.25, 1)',
      elegant: 'cubic-bezier(0.23, 1, 0.32, 1)',
      bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
    },
    // Keyframe definitions for use in components
    keyframes: {
      fadeIn: `
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `,
      fadeInUp: `
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `,
      fadeInDown: `
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `,
      slideInRight: `
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(-24px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `,
      scaleIn: `
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `,
      shimmer: `
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `,
      pulse: `
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `,
      float: `
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
      `
    }
  },

  breakpoints: {
    xs: '475px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px'
  },

  zIndex: {
    0: 0,
    10: 10,
    20: 20,
    30: 30,
    40: 40,
    50: 50,
    dropdown: 1000,
    sticky: 1020,
    modal: 1030,
    popover: 1040,
    tooltip: 1050,
    notification: 1060,
    overlay: 1070
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Responsive design helper
export const breakpoint = (size: keyof typeof adminTheme.breakpoints) =>
  `@media (min-width: ${adminTheme.breakpoints[size]})`;

// Transition helper with premium defaults
export const transition = (
  property = 'all',
  duration = adminTheme.animation.duration.base,
  easing = adminTheme.animation.easing.elegant
) => `${property} ${duration} ${easing}`;

// Focus styles for accessibility
export const focusRing = (color = adminTheme.colors.accent) => `
  outline: none;
  box-shadow: 0 0 0 2px ${adminTheme.colors.background}, 0 0 0 4px ${color}40;
`;

// Text truncation
export const truncate = `
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// Multi-line truncation
export const lineClamp = (lines: number) => `
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// ============================================
// LUXURY CARD STYLES
// ============================================

// Standard luxury card
export const luxuryCard = `
  background: ${adminTheme.colors.white};
  border-radius: ${adminTheme.borderRadius.xl};
  box-shadow: ${adminTheme.shadows.luxurySm};
  border: 1px solid ${adminTheme.colors.border};
  transition: ${transition('all', adminTheme.animation.duration.moderate)};

  &:hover {
    box-shadow: ${adminTheme.shadows.luxuryMd};
    border-color: ${adminTheme.colors.borderAccent};
  }
`;

// Elevated luxury card
export const luxuryCardElevated = `
  background: ${adminTheme.colors.white};
  border-radius: ${adminTheme.borderRadius.xl};
  box-shadow: ${adminTheme.shadows.luxuryMd};
  border: 1px solid transparent;
  transition: ${transition('all', adminTheme.animation.duration.moderate)};

  &:hover {
    box-shadow: ${adminTheme.shadows.luxuryLg};
    transform: translateY(-2px);
  }
`;

// Simple card (legacy compatibility)
export const card = `
  background: ${adminTheme.colors.white};
  border-radius: ${adminTheme.borderRadius.lg};
  box-shadow: ${adminTheme.shadows.base};
  border: 1px solid ${adminTheme.colors.border};
`;

// Glass morphism with warm tones
export const glassMorphism = `
  background: rgba(254, 253, 251, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(233, 231, 227, 0.5);
`;

// Premium glass effect
export const luxuryGlass = `
  background: linear-gradient(
    135deg,
    rgba(254, 253, 251, 0.9) 0%,
    rgba(251, 249, 247, 0.85) 100%
  );
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(201, 162, 39, 0.1);
  box-shadow: ${adminTheme.shadows.luxurySm};
`;

// ============================================
// ACCENT & HIGHLIGHT STYLES
// ============================================

// Gold accent bar (for active states)
export const accentBar = `
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 60%;
    background: ${adminTheme.colors.gradientAccent};
    border-radius: ${adminTheme.borderRadius.full};
    opacity: 0;
    transition: ${transition('opacity')};
  }

  &.active::before,
  &[data-active="true"]::before {
    opacity: 1;
  }
`;

// Gold underline accent
export const accentUnderline = `
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 2px;
    background: ${adminTheme.colors.gradientAccent};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform ${adminTheme.animation.duration.moderate} ${adminTheme.animation.easing.elegant};
  }

  &:hover::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

// Subtle gold glow on hover
export const accentGlow = `
  transition: ${transition('box-shadow', adminTheme.animation.duration.moderate)};

  &:hover {
    box-shadow: ${adminTheme.shadows.glowAccent};
  }
`;

// ============================================
// ANIMATION HELPERS
// ============================================

// Staggered entrance animation
export const staggeredEntrance = (index: number, baseDelay = 50) => `
  opacity: 0;
  animation: fadeInUp ${adminTheme.animation.duration.slow} ${adminTheme.animation.easing.elegant} forwards;
  animation-delay: ${index * baseDelay}ms;
`;

// Entrance animation mixin
export const entranceAnimation = (
  type: 'fadeIn' | 'fadeInUp' | 'fadeInDown' | 'slideInRight' | 'scaleIn' = 'fadeInUp',
  duration = adminTheme.animation.duration.slow,
  delay = '0ms'
) => `
  opacity: 0;
  animation: ${type} ${duration} ${adminTheme.animation.easing.elegant} forwards;
  animation-delay: ${delay};
`;

// Shimmer loading effect
export const shimmerEffect = `
  background: linear-gradient(
    90deg,
    ${adminTheme.colors.warmGray100} 25%,
    ${adminTheme.colors.warmGray200} 50%,
    ${adminTheme.colors.warmGray100} 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
`;

// ============================================
// TYPOGRAPHY HELPERS
// ============================================

// Display heading style
export const displayHeading = `
  font-family: ${adminTheme.typography.fontFamily.display};
  font-weight: ${adminTheme.typography.fontWeight.semibold};
  letter-spacing: ${adminTheme.typography.letterSpacing.tight};
  line-height: ${adminTheme.typography.lineHeight.tight};
  color: ${adminTheme.colors.text};
`;

// Section title style
export const sectionTitle = `
  font-family: ${adminTheme.typography.fontFamily.sans};
  font-size: ${adminTheme.typography.fontSize.xs};
  font-weight: ${adminTheme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: ${adminTheme.typography.letterSpacing.wider};
  color: ${adminTheme.colors.textTertiary};
`;

// Body text style
export const bodyText = `
  font-family: ${adminTheme.typography.fontFamily.sans};
  font-size: ${adminTheme.typography.fontSize.base};
  font-weight: ${adminTheme.typography.fontWeight.normal};
  line-height: ${adminTheme.typography.lineHeight.relaxed};
  color: ${adminTheme.colors.textSecondary};
`;

// ============================================
// BUTTON STYLES
// ============================================

// Primary button
export const primaryButton = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${adminTheme.spacing[2]};
  padding: ${adminTheme.spacing[3]} ${adminTheme.spacing[5]};
  background: ${adminTheme.colors.primary};
  color: ${adminTheme.colors.textInverse};
  font-family: ${adminTheme.typography.fontFamily.sans};
  font-size: ${adminTheme.typography.fontSize.sm};
  font-weight: ${adminTheme.typography.fontWeight.medium};
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  cursor: pointer;
  transition: ${transition()};

  &:hover {
    background: ${adminTheme.colors.primaryLight};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    ${focusRing()}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Gold accent button
export const accentButton = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${adminTheme.spacing[2]};
  padding: ${adminTheme.spacing[3]} ${adminTheme.spacing[5]};
  background: ${adminTheme.colors.gradientAccent};
  color: ${adminTheme.colors.primary};
  font-family: ${adminTheme.typography.fontFamily.sans};
  font-size: ${adminTheme.typography.fontSize.sm};
  font-weight: ${adminTheme.typography.fontWeight.semibold};
  border: none;
  border-radius: ${adminTheme.borderRadius.lg};
  cursor: pointer;
  transition: ${transition()};

  &:hover {
    box-shadow: ${adminTheme.shadows.glowAccent};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus {
    ${focusRing(adminTheme.colors.accent)}
  }
`;

// ============================================
// INPUT STYLES
// ============================================

// Standard input
export const inputStyle = `
  width: 100%;
  padding: ${adminTheme.spacing[3]} ${adminTheme.spacing[4]};
  font-family: ${adminTheme.typography.fontFamily.sans};
  font-size: ${adminTheme.typography.fontSize.sm};
  color: ${adminTheme.colors.text};
  background: ${adminTheme.colors.white};
  border: 1px solid ${adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius.lg};
  transition: ${transition()};

  &::placeholder {
    color: ${adminTheme.colors.textMuted};
  }

  &:hover {
    border-color: ${adminTheme.colors.borderDark};
  }

  &:focus {
    outline: none;
    border-color: ${adminTheme.colors.accent};
    box-shadow: 0 0 0 3px ${adminTheme.colors.accentMuted};
  }

  &:disabled {
    background: ${adminTheme.colors.warmGray100};
    cursor: not-allowed;
  }
`;

// ============================================
// GLOBAL KEYFRAMES (inject once)
// ============================================

export const globalKeyframes = `
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-16px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(-24px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-4px); }
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

export default adminTheme;
